import { ChatSession } from '../models/chat-session.model';
import { ChatMessage } from '../models/chat-message.model';
import {
  ChatConversationDetailDto,
  ChatConversationMessageDto,
  ChatConversationSummaryDto,
} from '../models/chat-session-api.model';

export function buildChatId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

export function buildChatMessage(role: ChatMessage['role'], content: string): ChatMessage {
  return {
    id: buildChatId(role),
    role,
    content,
    timestamp: new Date().toISOString(),
  };
}

export function buildChatSession(title?: string): ChatSession {
  const now = new Date().toISOString();
  return {
    id: buildChatId('session'),
    title: title || 'New chat',
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function isEmptyDraftSession(session: ChatSession | null | undefined): boolean {
  return !!session && !session.conversationId && (!session.messages || session.messages.length === 0);
}

export function compactEmptyDraftSessions(sessions: ChatSession[], activeId?: string | null): ChatSession[] {
  const activeEmptyDraft = sessions.find(
    (session) => session.id === activeId && isEmptyDraftSession(session),
  );
  let keptEmptyDraft = false;

  return sessions.filter((session) => {
    if (!isEmptyDraftSession(session)) {
      return true;
    }

    if (activeEmptyDraft) {
      return session.id === activeEmptyDraft.id;
    }

    if (keptEmptyDraft) {
      return false;
    }

    keptEmptyDraft = true;
    return true;
  });
}

export function normalizeChatSessionTitle(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) {
    return 'New chat';
  }
  const words = trimmed.split(/\s+/).slice(0, 10).join(' ');
  const capped = words.length > 60 ? `${words.slice(0, 57).trim()}...` : words;
  return words.length !== trimmed.length ? `${capped}...` : capped;
}

export function mergeChatSessions(localDrafts: ChatSession[], remoteSessions: ChatSession[]): ChatSession[] {
  return [...localDrafts, ...remoteSessions].sort((left, right) => {
    const leftTime = Date.parse(left.updatedAt || left.createdAt || '') || 0;
    const rightTime = Date.parse(right.updatedAt || right.createdAt || '') || 0;
    return rightTime - leftTime;
  });
}

export function mapConversationSummary(summary: ChatConversationSummaryDto): ChatSession {
  const conversationId = `${summary.id || ''}`.trim();
  return {
    id: conversationId,
    conversationId,
    title: `${summary.title || 'New chat'}`.trim() || 'New chat',
    messages: [],
    createdAt: summary.createdAt || new Date().toISOString(),
    updatedAt: summary.updatedAt || summary.lastMessageAt || summary.createdAt || new Date().toISOString(),
  };
}

export function mapConversationDetail(
  detail: ChatConversationDetailDto,
  existingSessionId?: string,
): Partial<ChatSession> {
  const conversationId = `${detail.id || ''}`.trim();
  return {
    id: existingSessionId || conversationId,
    conversationId,
    title: `${detail.title || 'New chat'}`.trim() || 'New chat',
    messages: Array.isArray(detail.messages) ? detail.messages.map((message) => mapConversationMessage(message)) : [],
    createdAt: detail.createdAt || new Date().toISOString(),
    updatedAt: detail.updatedAt || detail.createdAt || new Date().toISOString(),
  };
}

export function mapConversationMessage(message: ChatConversationMessageDto): ChatMessage {
  return {
    id: `${message.id || buildChatId(message.role || 'message')}`.trim(),
    role: (message.role || 'assistant') as ChatMessage['role'],
    content: `${message.content || ''}`,
    timestamp: message.timestamp || new Date().toISOString(),
    responseRating: (message.responseRating as 'good' | 'bad' | null | undefined) || null,
    interactive: message.interactive || null,
  };
}
